import { Button, Chip, InputLabel, MenuItem } from '@mui/material';
import { Field, useField } from 'formik';
import isRelativeUrl from 'is-relative-url';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { boxVariants } from '../../../../games/common/box-variants';
import { Trigger } from '../../../../games/common/trigger';
import { getWixResizedImage, useAppDispatch } from '../../../../lib';
import { closeSidebar, openSidebar } from '../../../../store';
import { TriggerOpts, TriggerPosition } from '../../../../types';

import { CheckboxField, FormRowsDivider, ImagePickerField, InputField } from '../../../fields';

const triggerPositions: ReadonlyArray<TriggerPosition> = ['bottom-left', 'bottom-right', 'top-left', 'top-right'];

type TriggerSettingsProps = {};

export const TriggerSettings: FC<TriggerSettingsProps> = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<'desktop' | 'mobile'>('desktop');
  const fieldName = `campaign.displayRules.trigger.${view}`;
  const [triggerField] = useField<TriggerOpts>(fieldName);
  const dispatch = useAppDispatch();
  const [triggerPreviewVisible, setTriggerPreviewVisible] = useState(false);

  const imageUrl = isRelativeUrl(triggerField.value.imageUrl)
    ? getWixResizedImage(triggerField.value.imageUrl, 300, 300)
    : triggerField.value.imageUrl;

  const closeSideBar = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  const handleTriggerImageClick = useCallback(() => {
    dispatch(
      openSidebar({
        content: (
          <Field
            name={`${fieldName}.imageUrl`}
            imageVariants={boxVariants}
            handleWixModalOpened={closeSideBar}
            component={ImagePickerField}
            uploadUrl="/files-uploader/upload?type=trigger"
          />
        ),
      })
    );
  }, [closeSideBar, dispatch, fieldName]);

  return (
    <>
      <FormRowsDivider />

      <TagsWrapper>
        <Chip
          label="Desktop"
          onClick={() => {
            setView('desktop');
          }}
          variant={view === 'desktop' ? 'filled' : 'outlined'}
          color="primary"
        />
        <Chip
          label="Mobile"
          onClick={() => {
            setView('mobile');
          }}
          variant={view === 'mobile' ? 'filled' : 'outlined'}
          color="primary"
        />
      </TagsWrapper>

      <TriggerSettingsWrapper>
        <TriggerColumn>
          <InputLabel>{t('pages.campaign.triggers.triggerImage')}</InputLabel>
          <TriggerPreview imageUrl={imageUrl} onClick={handleTriggerImageClick} />
        </TriggerColumn>

        <TriggerColumn>
          <Row>
            <Field
              name={`${fieldName}.position`}
              component={InputField}
              label={t('pages.campaign.triggersColumn.triggerPosition')}
              select
              className="row-field"
            >
              {triggerPositions.map((position) => (
                <MenuItem key={position} value={position}>
                  {position}
                </MenuItem>
              ))}
            </Field>

            <Field
              name={`${fieldName}.rotate`}
              component={InputField}
              label={t('pages.campaign.triggersColumn.triggerRotate')}
              type="number"
              className="row-field"
            />
          </Row>

          <Row>
            <Field
              name={`${fieldName}.width`}
              component={InputField}
              label={t('pages.campaign.triggersColumn.triggerWidth')}
              type="number"
              className="row-field"
            />

            <Field
              name={`${fieldName}.height`}
              component={InputField}
              label={t('pages.campaign.triggersColumn.triggerHeight')}
              type="number"
              className="row-field"
            />
          </Row>

          <Row>
            <Field
              name={`${fieldName}.shiftX`}
              component={InputField}
              label={t('pages.campaign.triggersColumn.triggerHorizontal')}
              type="number"
              className="row-field"
            />

            <Field
              name={`${fieldName}.shiftY`}
              component={InputField}
              label={t('pages.campaign.triggersColumn.triggerVertical')}
              type="number"
              className="row-field"
            />
          </Row>

          <Field
            name={`${fieldName}.animation`}
            component={CheckboxField}
            label={t('pages.campaign.triggersColumn.triggerUseAnimation')}
          />

          <ShowTriggerDemoButton
            onClick={() => {
              setTriggerPreviewVisible((visible) => !visible);
            }}
            variant="outlined"
            size="small"
          >
            {triggerPreviewVisible ? t('pages.campaign.triggersColumn.triggerButtonHide') : t('pages.campaign.triggersColumn.triggerButtonShow')}
          </ShowTriggerDemoButton>
        </TriggerColumn>
      </TriggerSettingsWrapper>

      {triggerPreviewVisible && <Trigger {...triggerField.value} />}
    </>
  );
};

const TriggerSettingsWrapper = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const TriggerColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 64px;

  :last-child {
    margin-right: 0;
  }
`;

const Row = styled.div`
  display: flex;
  margin-bottom: 24px;

  :last-of-type {
    margin-bottom: 12px;
  }
`;

const ShowTriggerDemoButton = styled(Button)`
  margin-top: 12px;
`;

const TagsWrapper = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
`;

const TriggerPreview = styled.div<{ imageUrl: string }>`
  width: 140px;
  height: 140px;
  margin-top: 16px;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url(${(p) => p.imageUrl});
  background-position: center;
  cursor: pointer;

  :hover {
    box-shadow: 0px 0px 0 1px rgb(0 0 0 / 25%);
  }
`;
